import {
	Box,
	Button,
	IconButton,
	makeStyles,
	useMediaQuery,
	useTheme,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PeopleIcon from "@material-ui/icons/People";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import AuthSignOutModal from "Components/Auth/AuthSignOutModal";
import { useAuthSignOutModal } from "Components/Auth/hooks/useAuthSignOutModal";
import { useAuthUser } from "Components/Auth/hooks/useAuthUser";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { DIALOG_ROOT_CLASS_NAME } from "Components/Modal/Modal";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import { getIsDayViewEnabled } from "Components/ProfileLimit/helpers/getIsDayViewEnabled";
import { useSchedule } from "Components/Schedule/hooks/useSchedule";
import { useScheduleResources } from "Components/ScheduleResource/hooks/useScheduleResources";
import { useTeamsModal } from "Components/Team/hooks/useTeamsModal";
import { useWorkingHoursModal } from "Components/WorkingHours/hooks/useWorkingHoursModal";
import { color, px } from "Configs/theme";
import { useApp } from "Providers/AppProvider";
import { useTranslation } from "Providers/TranslationProvider";
import React, { FC, Fragment, useEffect, useState } from "react";
import NavigationCalendarMonthView from "./NavigationCalendarMonthView";
import NavigationCalendarWeekView from "./NavigationCalendarWeekView";
import { CalendarType, useTranslateCalendarType } from "./NavigationHelper";
import NavigationCalendarDayView from "./NavigationCalendarDayView";
import moment from "moment";
import { KeyboardDatePicker } from "@material-ui/pickers";

interface Props {
	cellSize: CellSize;
}

const NavigationView: FC<Props> = (props) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const isInternalAdmin = useIsInternalAdmin();
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const userId = useAuthUser((state) => state.user?.uid);
	const profileLimits = useProfileLimits((state) => state.items);

	const classes = useStyles({
		isSmallScreen,
		navigationAppHeight: props.cellSize.navigation.appHeight,
		calendarTypeJustifyContent: isInternalAdmin ? "center" : "flex-end",
	});
	const calendarType = useCalendar((state) => state.calendarType);
	const selectedDate = useCalendar((state) => state.selectedDate);
	const isInitializingScheduleResources = useScheduleResources(
		(state) => state.isInitializing
	);
	const isInitializingSchedule = useSchedule((state) => state.isInitializing);
	const { shadowRoot, isStandaloneApp } = useApp();
	const { t } = useTranslation();
	const { isNative } = useApp();
	const startOfWeek = useCalendar((state) => state.startOfWeek);
	const weekPlus = useCalendar((state) => state.weekPlus);
	const translateCalendarType = useTranslateCalendarType();

	function getAvailableCalendarTypes(): CalendarType[] {
		const baseTypes = [CalendarType.WEEK, CalendarType.MONTH];
		
		if (userId && getIsDayViewEnabled(userId, profileLimits)) {
			return [CalendarType.DAY, ...baseTypes];
		}
		
		return baseTypes;
	}

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (!shadowRoot?.querySelector(`.${DIALOG_ROOT_CLASS_NAME}`)) {
				if (event.key === "ArrowLeft") {
					if (calendarType === CalendarType.DAY) {
						useCalendar.getState().setSelectedDate(selectedDate.clone().subtract(1, 'day'));
					} else {
						useCalendar.getState().setWeekPlus(weekPlus - 1);
					}
				} else if (event.key === "ArrowRight") {
					if (calendarType === CalendarType.DAY) {
						useCalendar.getState().setSelectedDate(selectedDate.clone().add(1, 'day'));
					} else {
						useCalendar.getState().setWeekPlus(weekPlus + 1);
					}
				}
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [weekPlus, shadowRoot, calendarType, selectedDate]);

	function handleDateChange(date: moment.Moment | null) {
		if (date && date.isValid()) {
			useCalendar.getState().setSelectedDate(date);
		}
		setIsDatePickerOpen(false);
	}

	function renderPeopleButton() {
		if (isInitializingScheduleResources) return;
		if (isInitializingSchedule) return;

		return (
			<Button
				className={classes.actionButton}
				color="primary"
				variant="contained"
				size="small"
				startIcon={!isSmallScreen && <PeopleIcon />}
				onClick={() => useTeamsModal.getState().open()}
			>
				{isSmallScreen ? (
					<PeopleIcon />
				) : (
					t("CalendarNavigationView_Button_EditTeam")
				)}
			</Button>
		);
	}

	function renderAppNavigation() {
		return (
			<Box className={classes.appNavigation}>
				<Button
					className={classes.chevronButton}
					variant="outlined"
					onClick={() => {
						if (calendarType === CalendarType.DAY) {
							useCalendar.getState().setSelectedDate(selectedDate.clone().subtract(1, 'day'));
						} else {
							useCalendar.getState().setWeekPlus(weekPlus - 1);
						}
					}}
				>
					<ChevronLeftIcon />
				</Button>
				<Button
					className={classes.todayButton}
					variant="outlined"
					onClick={() => {
						if (calendarType === CalendarType.DAY) {
							useCalendar.getState().setSelectedDate(moment().startOf('day'));
						} else {
							useCalendar.getState().setWeekPlus(0);
						}
					}}
				>
					{t("Common_Today")}
				</Button>
				<Button
					className={classes.chevronButton}
					variant="outlined"
					onClick={() => {
						if (calendarType === CalendarType.DAY) {
							useCalendar.getState().setSelectedDate(selectedDate.clone().add(1, 'day'));
						} else {
							useCalendar.getState().setWeekPlus(weekPlus + 1);
						}
					}}
				>
					<ChevronRightIcon />
				</Button>
				{!isSmallScreen && (
					<Box className={classes.week}>
						{calendarType === CalendarType.DAY ? (
							<Box className={classes.dateSelector}>
								<span>{selectedDate.format("ddd, MMMM D, YYYY")}</span>
								<IconButton 
									size="small" 
									onClick={() => setIsDatePickerOpen(true)}
									className={classes.calendarIcon}
								>
									<CalendarTodayIcon fontSize="small" />
								</IconButton>
								<KeyboardDatePicker
									variant="dialog"
									open={isDatePickerOpen}
									onClose={() => setIsDatePickerOpen(false)}
									value={selectedDate}
									onChange={handleDateChange}
									format="MM/DD/YYYY"
									style={{ display: 'none' }}
									DialogProps={{
										className: classes.datePickerDialog
									}}
									cancelLabel={t("Common_Dialog_Close")}
									okLabel={t("Common_Dialog_Done")}
									autoOk
								/>
							</Box>
						) : (
							<>
								{t("Common_Week", {
									week: startOfWeek.week(),
								})}
								{" - "}
								{calendarType === CalendarType.WEEK
									? startOfWeek.format("MMMM YYYY")
									: t("Common_Week", {
											week: startOfWeek
												.clone()
												.add(props.cellSize.month.weeksLength - 1, "weeks")
												.week(),
									  })}
							</>
						)}
					</Box>
				)}
				{!isSmallScreen && !isNative && (
					<Box className={classes.calendarType}>
						<ToggleButtonGroup
							exclusive
							value={calendarType}
							onChange={(_, next: CalendarType | null) => {
								if (next === null) return;
								useCalendar.getState().setCalendarType(next);
							}}
						>
							{getAvailableCalendarTypes().map(
								(calendarType, index) => (
									<ToggleButton
										className={classes.calendarTypeToggleButton}
										key={index}
										value={calendarType}
									>
										{translateCalendarType(calendarType)}
									</ToggleButton>
								)
							)}
						</ToggleButtonGroup>
					</Box>
				)}
				{!isNative && (
					<Box className={classes.actions}>
						{isInternalAdmin && (
							<Fragment>
								{renderPeopleButton()}
								<Button
									className={classes.actionButton}
									color="primary"
									variant="contained"
									size="small"
									onClick={useWorkingHoursModal.getState().open}
									startIcon={!isSmallScreen && <ScheduleIcon />}
								>
									{isSmallScreen ? (
										<ScheduleIcon />
									) : (
										t("CalendarNavigationView_Button_EditSettings")
									)}
								</Button>
							</Fragment>
						)}
						{isStandaloneApp && (
							<IconButton
								className={classes.actionIconButton}
								onClick={useAuthSignOutModal.getState().open}
							>
								<ExitToAppIcon />
							</IconButton>
						)}
					</Box>
				)}
			</Box>
		);
	}

	function renderCalendarNavigation() {
		if (calendarType === CalendarType.MONTH) {
			return <NavigationCalendarMonthView cellSize={props.cellSize} />;
		} else if (calendarType === CalendarType.DAY && userId && getIsDayViewEnabled(userId, profileLimits)) {
			// Hide NavigationCalendarDayView on mobile devices
			if (isSmallScreen) {
				return null;
			}
			return <NavigationCalendarDayView cellSize={props.cellSize} />;
		} else {
			return <NavigationCalendarWeekView cellSize={props.cellSize} />;
		}
	}

	return (
		<Fragment>
			<Box
				className={classes.root}
				top={props.cellSize.offset.top}
				left={props.cellSize.offset.left}
			>
				{renderAppNavigation()}
				{renderCalendarNavigation()}
			</Box>
			{isStandaloneApp && <AuthSignOutModal />}
		</Fragment>
	);
};

export default NavigationView;

interface StyleProps {
	isSmallScreen: boolean;
	navigationAppHeight: number;
	calendarTypeJustifyContent: string;
}

const useStyles = makeStyles({
	root: {
		position: "fixed",
		backgroundColor: color.totalBackground,
		zIndex: 3,
		right: 0,
	},
	appNavigation: (props: StyleProps) => ({
		padding: `0 ${px.xl}`,
		height: props.navigationAppHeight,
		display: "flex",
		alignItems: "center",
	}),
	todayButton: (props: StyleProps) => ({
		display: "flex",
		flex: props.isSmallScreen ? 4 : undefined,
		margin: `0 ${px.s}`,
	}),
	chevronButton: (props: StyleProps) => ({
		display: "flex",
		flex: props.isSmallScreen ? 1 : undefined,
		padding: `${px.xs} 0`,
		minWidth: "46px",
	}),
	week: {
		paddingLeft: px.xl,
		display: "flex",
		alignItems: "center",
		padding: `${px.m} 0`,
		letterSpacing: "1px",
		fontSize: "16px",
		fontWeight: "bold",
	},
	dateSelector: {
		display: "flex",
		alignItems: "center",
		cursor: "pointer",
	},
	calendarIcon: {
		padding: "2px",
		marginLeft: "4px",
	},
	datePickerDialog: {
		zIndex: 2000,
	},
	calendarType: (props: StyleProps) => ({
		display: "flex",
		flex: 1,
		justifyContent: props.calendarTypeJustifyContent,
		marginLeft: px.s,
	}),
	calendarTypeToggleButton: {
		height: 28,
	},
	actions: {
		display: "flex",
		alignItems: "center",
	},
	actionIconButton: {
		marginLeft: px.s,
	},
	actionButton: {
		marginLeft: px.s,
		height: 34,
	},
});
